import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ScrollToTop from "@/components/feature/ScrollToTop";
import ArticleCard from "@/components/feature/ArticleCard";
import SchemaOrg from "@/components/feature/SchemaOrg";
import { articles, type Article } from "@/mocks/articles";
import { categories } from "@/mocks/categories";

function getCategoryName(slug: string): string {
  const cat = categories.find((c) => c.slug === slug);
  return cat ? cat.name : slug;
}

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === id);
  const relatedArticles = article
    ? articles.filter((a) => a.category === article.category && a.id !== article.id).slice(0, 3)
    : [];

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-20 text-center">
            <div className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 mx-auto mb-4">
              <i className="ri-file-unknow-line text-2xl text-foreground-400"></i>
            </div>
            <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-2">文章未找到</h1>
            <p className="text-foreground-500 mb-6">该文章可能已被移除或链接有误</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const categoryName = getCategoryName(article.category);

  return (
    <>
      <ScrollToTop />
      <SchemaOrg
        type="Article"
        data={{
          headline: article.title,
          description: article.excerpt,
          image: article.image,
          datePublished: article.date,
          author: { "@type": "Person", name: article.author },
          publisher: {
            "@type": "Organization",
            name: "探花精选",
          },
          mainEntityOfPage: {
            "@type": "WebPage",
            "@id": `https://pkgogo.cn/article/${article.id}`,
          },
        }}
      />
      <SchemaOrg
        type="BreadcrumbList"
        data={{
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "首页", item: "https://pkgogo.cn" },
            { "@type": "ListItem", position: 2, name: categoryName, item: `https://pkgogo.cn/category/${article.category}` },
            { "@type": "ListItem", position: 3, name: article.title },
          ],
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: "首页", href: "/" },
                { label: categoryName, href: `/category/${article.category}` },
                { label: article.title },
              ]}
            />
          </div>

          <article className="pb-14 md:pb-20">
            {/* Article Header */}
            <header className="max-w-3xl mx-auto px-4 md:px-6 py-8 md:py-12">
              <div className="flex items-center gap-3 mb-4">
                <Link
                  to={`/category/${article.category}`}
                  className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-primary-50 text-primary-600 text-xs font-medium hover:bg-primary-100 transition-colors whitespace-nowrap"
                >
                  {categoryName}
                </Link>
                {article.tags.slice(0, 2).map((tag) => (
                  <span key={tag} className="text-xs text-foreground-400">
                    #{tag}
                  </span>
                ))}
              </div>
              <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 leading-tight">
                {article.title}
              </h1>
              <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-line"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line"></i>
                  {article.date}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line"></i>
                  {article.readTime}分钟阅读
                </span>
              </div>
            </header>

            {/* Featured Image */}
            <div className="max-w-4xl mx-auto px-4 md:px-6 mb-10">
              <div className="rounded-lg overflow-hidden">
                <img
                  src={article.image}
                  alt={article.title}
                  title={article.title}
                  className="w-full object-cover"
                  style={{ maxHeight: "500px" }}
                />
              </div>
            </div>

            {/* Article Content */}
            <div className="max-w-3xl mx-auto px-4 md:px-6">
              <div
                className="prose prose-lg max-w-none
                  prose-headings:font-heading prose-headings:text-foreground-900 prose-headings:font-semibold
                  prose-h2:text-xl md:prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4
                  prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:mb-5
                  prose-strong:text-foreground-900 prose-strong:font-semibold
                  prose-a:text-primary-500 prose-a:no-underline hover:prose-a:text-primary-600
                  prose-img:rounded-lg"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />
            </div>

            {/* Tags */}
            <div className="max-w-3xl mx-auto px-4 md:px-6 mt-10 pt-8 border-t border-background-200/70">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm text-foreground-500">标签：</span>
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 rounded-full bg-background-100 text-foreground-600 text-xs hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          </article>

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <section className="py-10 md:py-14 bg-background-100">
              <div className="max-w-7xl mx-auto px-4 md:px-6">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6">
                  相关推荐
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
                  {relatedArticles.map((rel) => (
                    <ArticleCard key={rel.id} article={rel} size="small" />
                  ))}
                </div>
              </div>
            </section>
          )}
        </main>

        <Footer />
      </div>
    </>
  );
}